import React, { useState, useCallback, useEffect, useRef, FormEvent } from "react";
import { useApi } from "../../../hooks/api";
import { Container, ContainerButtons, ContainerIDescription, ContainerIntegrationLogo, Content, ContentFirstStep, Form } from "./style";
import { useGlobal } from "../../../hooks/global";
import { ModalHeader } from "../../../components/ModalHeader";
import { ModalGeneric } from "../../../components/ModalGeneric";
import { ContentOrLoading } from "../../../components/Loadings/ContentOrLoading";
import { IGroupProps } from "../../../interfaces";
import { useForm } from "react-hook-form";
import { GenericForm, IGenericFormRefProps } from "../../../components/GenericForm";
import Button from "../../../components/Buttons/Button";
import { activeItens, secondary } from "../../../theme";
import magikeyIcon from "./../../../assets/magikey.svg";
import { Flex } from "@mantine/core";
import { apiUrlMagikey } from "../constants/apiUrlMagikey";
import { FaRegArrowAltCircleRight } from "react-icons/fa";

interface IModalMagikeyConection {
    onCancel: Function
    onSave: Function
    defaultId?: string
}

export const ModalMagikeyConection: React.FC<IModalMagikeyConection> = (props) => {
    const { onCancel, onSave, defaultId } = props

    const { api } = useApi()
    const { theme, notify } = useGlobal()

    const [loading, setLoading] = useState(false);
    const [stepCreateIntegration, setStepCreateIntegration] = useState(!defaultId ? 1 : 2);

    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
        setError,
        setValue,
        watch,
        trigger
    } = useForm({
        mode: "all",
    });

    const _form = watch();

    const form_ref = useRef<IGenericFormRefProps>(null);
    const submitButtonRef = useRef<HTMLButtonElement>(null);

    const searchIntegration = useCallback(async () => {
        if (defaultId) {
            setLoading(true);
            try {
                const { data } = await api.get(`/integrations/${defaultId}`);

                Object.entries(data).forEach(([key, value]) => {
                    setValue(key, value);
                });

            } catch (err) {
                notify("Erro ao buscar a integração", "error");
                onCancel();
            }
        }

        setLoading(false);
    }, [defaultId, setValue]);

    const onSubmit = useCallback(async () => {
        setLoading(true);

        const api_url = apiUrlMagikey;
        const api_key = _form?.api_key;
        const type = 'MAGIKEY';
        const active = true;

        try {
            await api.post("/integrations", {
                api_url,
                api_key,
                type,
                active
            })

            notify('Dados da integração com Magikey salvos com sucesso!', "success");

            onSave();
        } catch (err: any) {
            const error = err.response ? err.response?.data : "SERVER ERROR";

            if (error?.field === 'api_key') {
                notify(error?.detail, "alert");
                setLoading(false);
                return;
            }

            if (error?.field) {
                setError(error.field, { type: "manual", message: error.detail });
            } else {
                notify('Erro ao salvar informações da integração com Magike, tente novamente mais tarde!', "alert");
            }
        }

        setLoading(false);
    }, [api, errors, _form]);

    const handleStopPropagation = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e?.stopPropagation();
            handleSubmit(onSubmit)(e);
        },
        [handleSubmit, onSubmit, form_ref, _form, api]
    );

    const groups: IGroupProps[] = [
        {
            name: "",
            label: "",
            fields: [
                [
                    {
                        name: "api_key",
                        label: "Api token",
                        type: "input",
                        required: true,
                    },
                ],
            ],
        },
    ];

    useEffect(() => {
        searchIntegration();
    }, [defaultId]);

    return (
        <ModalGeneric open onClose={() => { }}>
            <Container theme={theme}>
                <ModalHeader
                    title={"Integração com o Magikey"}
                    subTitle={"Informe os dados necessárias para realizar integração com a Magikey"}
                    onCancel={() => onCancel()}
                    theme={theme}
                />

                {stepCreateIntegration === 1 && (
                    <ContentFirstStep>
                        <ContainerIntegrationLogo>
                            <img src={magikeyIcon} alt="Stays" style={{ width: 30, objectFit: "contain" }} />
                            Magikey
                        </ContainerIntegrationLogo>

                        <ContainerIDescription>
                            🔐 Bem-vindo à integração Magikey + WebCheckin.app!
                            Essa integração foi pensada para elevar sua operação a outro nível, conectando automação de check-in com o controle de acesso inteligente da Magikey.
                            <br />
                            <br />
                            Com a integração ativa, você poderá:
                            <br />
                            <br />
                            ✅ Gerar chaves virtuais automaticamente para cada reserva confirmada; <br />
                            ✅ Liberar o acesso dos hóspedes apenas dentro do período da estadia; <br />
                            ✅ Criar códigos únicos e temporários para fechaduras eletrônicas Magikey; <br />
                            ✅ Reduzir o contato físico e garantir mais segurança na entrada e saída; <br />
                            ✅ Ativar ou revogar acessos diretamente pelo painel do WebCheckin.app. <br />
                            <br />
                            <br />
                            🚀 Pronto para ativar a integração?
                            Conecte sua conta Magikey e comece a oferecer uma experiência de check-in mais inteligente, ágil e profissional aos seus hóspedes.
                            <br />
                            <br />
                            👉 Clique em “Iniciar” para configurar.
                        </ContainerIDescription>

                        <Flex justify='flex-end'>
                            <Button
                                onClick={() => setStepCreateIntegration(2)}
                                background={secondary[theme]}
                                color={"#FFF"}
                                style={{ padding: 8, fontSize: 14, marginTop: 10, marginBottom: 15 }}
                                children={"INICIAR"}
                            />
                        </Flex>
                    </ContentFirstStep>
                )}

                {stepCreateIntegration === 2 && (
                    <ContentOrLoading loading={loading} theme={theme} type="wave">
                        <Content>
                            <ContainerIntegrationLogo>
                                <img src={magikeyIcon} alt="Stays" style={{ width: 30, objectFit: "contain" }} />
                                Magikey
                            </ContainerIntegrationLogo>

                            <ContainerIDescription>
                                <div style={{ fontSize: '18px', fontWeight: 600, paddingBottom: 6 }}>Dados de Acesso</div>
                                Para realizar a integração, é necessário que você gere um Token no Magikey.
                                <a
                                    href="https://www.youtube.com/watch?v=SOkhBY5MLZI"
                                    target="_blank"
                                    style={{ textDecoration: 'none', color: activeItens[theme], padding: '0 5px' }}
                                >
                                    Clique aqui
                                </a>
                                para assistir ao passo a passo de como gerar o seu Token e ativar a integração.
                            </ContainerIDescription>

                            <Form onSubmit={handleStopPropagation}>
                                <GenericForm
                                    ref={form_ref}
                                    groups={groups}
                                    _form={_form}
                                    control={control}
                                    trigger={trigger}
                                    errors={errors}
                                    setValue={setValue}
                                    register={register}
                                    key={errors?.toString()}
                                />
                                <button
                                    className="display-none"
                                    type="submit"
                                    ref={submitButtonRef}
                                />
                            </Form>
                        </Content>

                        <ContainerButtons theme={theme}>
                            <Button
                                onClick={() => submitButtonRef.current?.click()}
                                background={secondary[theme]}
                                color={"#FFF"}
                                style={{ padding: 8, maxWidth: 150, fontSize: 14 }}
                                children={
                                    <Flex align='center' gap={7}>
                                        Validar Dados

                                        <FaRegArrowAltCircleRight />
                                    </Flex>
                                }
                            />
                        </ContainerButtons>
                    </ContentOrLoading>
                )}
            </Container>
        </ModalGeneric>
    )
}